import { Button, Modal, Spinner, TextField, Typography, Icon } from '@scale/scaleui';
import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import apiFetch from '@scale/frontend-lib/api-fetch';
import Link from '@scale/frontend-lib/link';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import { DuplicateJobPostResponse } from '@scale/shared-types';

interface DuplicateJobPostModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobName: string;
  jobPostId: string;
}

export const DuplicateJobPostModal: React.FC<DuplicateJobPostModalProps> = ({
  isOpen,
  onClose,
  jobName,
  jobPostId,
}) => {
  const [name, setName] = useState<string>('');
  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  useEffect(() => {
    if (jobName) {
      setName(`${jobName} (copy)`);
    }
  }, [jobName]);

  const queryClient = useQueryClient();
  const { mutate, data, error, isLoading, isError } = useMutation<
    DuplicateJobPostResponse,
    { response?: { message: string }; message: string },
    { jobPostId: string; newInternalJobName: string }
  >(
    ({ jobPostId, newInternalJobName }) =>
      apiFetch<DuplicateJobPostResponse>(null, `corp-api/job-post/duplicate`, {
        method: 'POST',
        body: {
          jobPostId,
          newInternalJobName,
        },
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('greenhouseJobPosts');
        queryClient.invalidateQueries(['all-jobs']);
      },
    },
  );

  const handleSubmit = () => {
    mutate({ jobPostId, newInternalJobName: name });
    setHasSubmitted(true);
  };

  return (
    <Modal
      open={isOpen}
      onClose={() => {
        if (!isLoading) {
          onClose();
          setHasSubmitted(false);
        }
      }}
      title={`Duplicate ${jobName}`}
    >
      <TextField
        className="mb-4"
        id="job-post-name"
        label={`New Internal Job Name`}
        placeholder={`${jobName} (copy)`}
        type="text"
        value={name}
        onChange={e => setName(e.target.value)}
      />
      <Typography.Paragraph3 className="mb-2">
        Note: only the most recent <b>saved</b> version will be copied. The duplicate will be
        inactive.
      </Typography.Paragraph3>
      <div className="flex flex-row items-center justify-end">
        {isLoading && <Spinner size="sm" className="mr-4" />}
        <Button disabled={isLoading || !name || hasSubmitted} onClick={handleSubmit}>
          Submit
        </Button>
      </div>
      {isError && error && (
        <p style={{ color: 'red' }} className="flex flex-col gap-y-2 my-2 text-xs">
          Failed to duplicate job post: {error.response?.message ?? error.message}
        </p>
      )}
      {data && (
        <div className="flex flex-row items-center">
          <Link
            href={`${Routes.JobPostSetup}/${data.newJobPostId}`}
            as={`/corp${Routes.JobPostSetup}/${data.newJobPostId}`}
          >
            <a target="_blank" className="flex items-center" rel="noopener noreferrer">
              {data.newInternalJobName}
              <Icon icon="external-link-alt" className="text-xs" size="sm" />
            </a>
          </Link>
        </div>
      )}
    </Modal>
  );
};
